"use client";

import { useMemo } from "react";
import Image from "next/image";
import { Header } from "@/components/quiz/explainers/header";
import { Button } from "@workspace/ui/components/button";
import explainersJson from "@/lib/data/explainers.json";

type DoctorCard = {
  subtitle: string;
  description: string;
  image: string;
  doctor_name: string;
  designation: string;
};

type RC1Model = {
  id: "regular-cycle-analysis-1";
  title: string;
  description: string;
  source?: string;
  card: DoctorCard[];
};

export default function RegularCycleExplainer1({
  title,
  image,
  image_description,
  handleNext,
}: {
  title: string;
  image?: string;
  image_description?: string;
  handleNext: () => void;
}) {
  const model = useMemo(() => {
    const all = (explainersJson as any)?.explainers ?? [];
    return all.find(
      (e: any) => e?.id === "regular-cycle-analysis-1"
    ) as RC1Model;
  }, []);

  const card = model.card?.[0];
  const alt = (card?.doctor_name || "expert").toString();

  return (
    <>
      <div className="flex flex-1 flex-col gap-6 justify-between overflow-y-auto min-h-0 pb-12">
        <div>
          <Header
            title={title}
            description={model.description}
            descriptionClassName="text-[#122D35] title-14"
          />

          <p className="title-14 pt-[16px] text-[#78848a]">
            {model.source}
          </p>
        </div>

        <div className="rounded-[16px] pb-3">
          <div className="p-5 bg-[#F3F4ED] flex flex-col gap-3 rounded-[16px]">
            {card?.subtitle ? (
              <h3 className="title-14 text-[#6E7F84]">
                {card.subtitle}
              </h3>
            ) : null}

            {card?.description ? (
              <p className="title-16 text-[#122D35]">
                {card.description}
              </p>
            ) : null}

            <div className="flex items-center gap-3">
              {card?.image ? (
                <div className="relative h-12 w-12 overflow-hidden rounded-full">
                  <Image
                    src={card.image}
                    alt={alt}
                    fill
                    sizes="48px"
                    className="object-cover"
                    style={{
                      transform: "scale(1.3)",
                      transformOrigin: "5% 30%",
                      objectPosition: "50% 68%",
                    }}
                    priority
                  />
                </div>
              ) : null}
              <div className="flex flex-col gap-0.5">
                {card?.doctor_name ? (
                  <p className="title-14 text-[#122D35]">
                    {card.doctor_name}
                  </p>
                ) : null}
                {card?.designation ? (
                  <p className="title-12 text-[#6E7F84]">
                    {card.designation}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="sticky bottom-0 flex flex-col gap-3 pb-6">
        <Button onClick={handleNext} variant="default" className="w-full shrink-0">See how Inito brings clarity</Button>
      </div>
    </>
  );
}
